/**
 * Approval Form — Field type registry
 *
 * Mỗi field type có:
 *  - value: string id (lưu vào DB)
 *  - label: hiển thị UI (VN)
 *  - icon: lucide component
 *  - group: tên nhóm trong panel "Thành phần"
 *  - isLayout?: true nếu là phần tử bố cục (không có giá trị data)
 *  - hasOptions?: true nếu cần config danh sách options
 *  - defaults?: () => object — config mặc định khi tạo field mới
 */
import {
  Type, AlignLeft, Hash, DollarSign, Phone, Mail, Link as LinkIcon,
  Calendar, CalendarRange, Clock,
  ChevronDown, ListChecks, CircleDot, CheckSquare,
  User as UserIcon, Building2,
  Image as ImageIcon, Paperclip,
  Calculator, ExternalLink, Table as TableIcon,
  AlignJustify, Minus, Heading2,
  Briefcase, Target, Folder, ListTodo, FileText, CreditCard, Users as UsersIcon,
  Eye, Bell, Flag,
  MapPin,
  Truck,
} from 'lucide-react';

import { msg } from "@lingui/core/macro";
import { tLabel } from "../../../i18n/runtime.js";
import { KY_HIEU_TIEN } from "../../../i18n/format.js";

export const FIELD_TYPES = [
  // ── A. Nhập liệu cơ bản ──
  { value: 'text', label: msg`Văn bản 1 dòng`, icon: Type, group: 'input' },
  { value: 'textarea', label: msg`Văn bản nhiều dòng`, icon: AlignLeft, group: 'input' },
  { value: 'number', label: msg`Số`, icon: Hash, group: 'input' },
  { value: 'amount', label: msg`Số tiền`, icon: DollarSign, group: 'input', defaults: () => ({ currency: defaultCurrency(), decimals: 0, in_words: false }) },
  { value: 'phone', label: msg`Số điện thoại`, icon: Phone, group: 'input' },
  { value: 'email', label: 'Email', icon: Mail, group: 'input' },
  { value: 'url', label: 'URL', icon: LinkIcon, group: 'input' },

  // ── B. Thời gian ──
  { value: 'date', label: msg`Ngày`, icon: Calendar, group: 'time', defaults: () => ({ with_time: false }) },
  { value: 'date_range', label: msg`Khoảng thời gian`, icon: CalendarRange, group: 'time', defaults: () => ({ with_time: false }) },

  // ── C. Lựa chọn ──
  { value: 'select', label: msg`Chọn 1 (dropdown)`, icon: ChevronDown, group: 'choice', hasOptions: true, defaults: () => ({ options: [{ value: 'opt1', label: msg`Tùy chọn 1` }] }) },
  { value: 'multi_select', label: msg`Chọn nhiều`, icon: ListChecks, group: 'choice', hasOptions: true, defaults: () => ({ options: [{ value: 'opt1', label: msg`Tùy chọn 1` }] }) },
  { value: 'radio', label: msg`Chọn 1 (inline)`, icon: CircleDot, group: 'choice', hasOptions: true, defaults: () => ({ options: [{ value: 'opt1', label: msg`Tùy chọn 1` }] }) },
  { value: 'checkbox', label: msg`Checkbox đơn`, icon: CheckSquare, group: 'choice' },

  // ── D. Người & Phòng ban ──
  { value: 'user_select', label: msg`Chọn người`, icon: UserIcon, group: 'people', defaults: () => ({ multi: false }) },
  { value: 'department_select', label: msg`Chọn phòng ban`, icon: Building2, group: 'people', defaults: () => ({ multi: false }) },

  // ── E. Tệp ──
  { value: 'image', label: msg`Hình ảnh`, icon: ImageIcon, group: 'files', defaults: () => ({ max_files: 10 }) },
  { value: 'file', label: msg`Tệp đính kèm`, icon: Paperclip, group: 'files', defaults: () => ({ max_files: 10 }) },

  // ── F. Vị trí (định vị GPS) ──
  // Mobile capture GPS + reverse geocode → server lưu {latitude, longitude,
  // address, captured_at, status}. Webadmin chỉ render read-only (link mở
  // Google Maps). Dùng cho Đơn xin ra ngoài, công tác, ...
  { value: 'location', label: msg`Vị trí GPS`, icon: MapPin, group: 'location', defaults: () => ({ auto_capture: true, allow_manual_edit: false }) },

  // ── F2. Tham chiếu chấm công ──
  // Read-only card hiển thị check-in/out hiện tại của user theo ngày user chọn
  // ở 1 date field cùng form. Dùng cho "Bổ sung chấm công" / OT — user thấy giờ
  // cũ → đối chiếu khi điền giờ mới. FE gọi GET /attendance/lookup-self?date=...
  // BE shared kernel format DTO (xem business-logic/attendanceLookup.js).
  // `source_date_field` = id của date field nguồn (PHẢI config qua builder).
  { value: 'attendance_lookup', label: msg`Tham chiếu chấm công`, icon: Clock, group: 'time',
    defaults: () => ({
      source_date_field: null,
      show: ['check_in_time', 'check_out_time', 'status'],
    }),
  },

  // ── F3. Phép còn lại (card chuyên kind 'leave') ──
  // Read-only card: đọc leave_type + leave_range → hiện số phép còn lại + cảnh báo khi
  // paid không đủ phép. FE gọi GET /leave-balance/me. Logic khớp kernel checkLeaveBalance.
  { value: 'leave_balance', label: msg`Phép còn lại (card)`, icon: CalendarRange, group: 'time',
    defaults: () => ({ source_type_field: null, source_range_field: null }) },

  // ── G. Nâng cao ──
  { value: 'formula', label: msg`Công thức`, icon: Calculator, group: 'advanced', defaults: () => ({ formula: '', formula_format: 'number', decimals: 0, ...(workspaceCurrency ? { currency: workspaceCurrency } : {}) }) },
  { value: 'reference', label: msg`Tham chiếu đơn khác`, icon: ExternalLink, group: 'advanced', defaults: () => ({ ref_template_id: null }) },
  { value: 'table', label: msg`Bảng / Chi tiết`, icon: TableIcon, group: 'advanced', defaults: () => ({ columns: [{ id: `c_${Date.now()}`, type: 'text', label: msg`Cột 1` }], min_rows: 1, max_rows: 0 }) },

  // ── G. Bố cục (không có data) ──
  { value: 'description', label: msg`Đoạn mô tả`, icon: AlignJustify, group: 'layout', isLayout: true, defaults: () => ({ content: msg`Nội dung hướng dẫn...` }) },
  { value: 'section', label: msg`Phân nhóm`, icon: Heading2, group: 'layout', isLayout: true },
  { value: 'divider', label: msg`Đường kẻ`, icon: Minus, group: 'layout', isLayout: true },

  // ── H. CRM ──
  { value: 'account_select', label: msg`Chọn khách hàng`, icon: Briefcase, group: 'crm', defaults: () => ({ multi: false }) },
  { value: 'company_select', label: msg`Chọn công ty`, icon: Building2, group: 'crm', defaults: () => ({ multi: false }) },
  { value: 'contact_select', label: msg`Chọn liên hệ`, icon: UsersIcon, group: 'crm', defaults: () => ({ multi: false }) },
  { value: 'deal_select', label: msg`Chọn cơ hội`, icon: Target, group: 'crm', defaults: () => ({ multi: false }) },
  { value: 'project_select', label: msg`Chọn dự án`, icon: Folder, group: 'crm', defaults: () => ({ multi: false }) },
  { value: 'task_select', label: msg`Chọn task`, icon: ListTodo, group: 'crm', defaults: () => ({ multi: false }) },
  { value: 'invoice_select', label: msg`Chọn hoá đơn`, icon: FileText, group: 'crm', defaults: () => ({ multi: false }) },
  { value: 'payment_select', label: msg`Chọn thanh toán`, icon: CreditCard, group: 'crm', defaults: () => ({ multi: false }) },

  // ── H2. Bằng chứng ra ngoài (composite) ──
  // 1 field, user chọn 1 trong 2 chế độ: upload ảnh minh chứng HOẶC link lead/deal
  // (đi gặp khách hàng). Value: mảng file [{url,name,size}] (legacy image list) hoặc
  // { mode: 'image', files: [...] } hoặc { mode: 'crm', entity_type: 'lead'|'deal',
  // entity_id, entity_label }. Với mode 'crm', BE Core tự tạo hoạt động Gặp khách hàng
  // (crm_activities type=meeting) trong lead/deal khi đơn được DUYỆT.
  { value: 'out_evidence', label: msg`Bằng chứng ra ngoài`, icon: ImageIcon, group: 'crm',
    defaults: () => ({ max_files: 10, allowed_sources: ['camera'] }) },

  // ── I. Tiện ích ──
  // supplier_select: chọn NCC từ module Tiện ích → snapshot auto vào form_data
  // dưới key `${field.id}__snapshot` để kế toán xem được thông tin tại thời điểm duyệt
  // dù sau đó NCC đổi MST/STK.
  { value: 'supplier_select', label: msg`Chọn nhà cung cấp`, icon: Truck, group: 'utility', defaults: () => ({ multi: false, snapshot: true }) },
];

export const FIELD_TYPE_BY_VALUE = Object.fromEntries(FIELD_TYPES.map(t => [t.value, t]));

/**
 * Ô chọn bản ghi CRM — picker + hiển thị gọi `/crm/*` (khách hàng · công ty · liên hệ · cơ hội · hoá đơn · thanh
 * toán). Hệ TẮT CRM (`modules.crm = false` ở @host/tenant — Site Connect 12/10/2026, xem lib/hostModules): panel
 * «Thành phần» không cho thêm, ô của mẫu cũ chỉ hiện giá trị đã lưu, form gửi đơn ẩn ô (FieldInput.isCrmFieldOff).
 * «Chọn dự án» / «Chọn task» / «Bằng chứng ra ngoài» cùng nhóm `crm` nhưng KHÔNG gọi /crm/* — không nằm ở đây.
 * Tệp này giữ thuần (không đọc @host/tenant): công cụ ngoài bundler đọc nó.
 */
export const CRM_RELATION_FIELD_TYPES = new Set([
  'account_select', 'customer_select', 'company_select', 'contact_select', 'deal_select', 'invoice_select', 'payment_select',
]);

/**
 * ── Ô NHẬP GIÁ TRỊ cho node Điều kiện (rẽ nhánh) ──
 *
 * Trước 17.09.2026 tab Quy trình chỉ có picker cho 3 meta-field «Người gửi · …»; MỌI
 * trường form đều rơi vào `<input type=text>` tự do. Hậu quả đo được trên builder:
 * - `department_select`: admin phải GÕ TAY UUID phòng ban (giao diện gửi đơn thì hiện
 *   cây phòng ban) ⇒ không ai gõ đúng ⇒ nhánh không bao giờ khớp, đơn rơi mặc định.
 * - `select`/`radio`/`multi_select`: phải gõ đúng `options[].value` (vd "opt1") chứ
 *   không phải nhãn đang thấy trên form ⇒ sai âm thầm.
 * - `image`/`file`/`table`/`location`/`attendance_lookup`/`leave_balance`/`out_evidence`:
 *   giá trị là mảng file / object, KHÔNG có dạng vô hướng để so sánh — hiện trong danh
 *   sách trường chỉ để tạo ra điều kiện chết.
 *
 * `kind` quyết định ô nhập + bộ toán tử hợp lệ (xem CONDITION_OPERATORS_BY_KIND ở
 * ProcessTab). `kind: null` = KHÔNG dùng được trong điều kiện.
 *
 * ⚠️ Giá trị gửi lên phải khớp thứ mà `evalRule` (backend approvalEngine.js) so sánh
 * với `formData[field_id]`: id thô cho picker, `options[].value` cho lựa chọn.
 */
const REL = (endpoint, labelKey = 'name') => ({ kind: 'relation', endpoint, labelKey });

export const CONDITION_VALUE_INPUT = {
  // Nhập liệu cơ bản — so chuỗi
  text: { kind: 'text' },
  textarea: { kind: 'text' },
  phone: { kind: 'text' },
  email: { kind: 'text' },
  url: { kind: 'text' },

  // Số — backend coerce Number() nên phải chặn người gõ chữ
  number: { kind: 'number' },
  amount: { kind: 'number' },
  formula: { kind: 'number' },

  date: { kind: 'date' },
  // date_range lưu OBJECT {from,to} nên bản thân nó không so sánh được (String(obj) =
  // "[object Object]"). Thay vì chặn hẳn, ô chọn trường BUNG nó ra thành các thuộc tính
  // so sánh được — `rule.field_id` thành đường dẫn `<id>.<suffix>`, kernel
  // @cnv/server-shared/business-logic/approvalRules (RANGE_PROPS) đọc đúng đường dẫn đó.
  // Cần vì date_range là kiểu của các trường KHOÁ theo loại đơn (leave_range, ot_range,
  // bt_range) — đúng những đơn hay rẽ nhánh nhất: "nghỉ quá 3 ngày thêm giám đốc",
  // "tăng ca quá 4 giờ trưởng phòng duyệt".
  date_range: {
    kind: null,
    reason: 'range',
    parts: [
      { suffix: 'from',  label: msg`Ngày bắt đầu`,        kind: 'date' },
      { suffix: 'to',    label: msg`Ngày kết thúc`,       kind: 'date' },
      { suffix: 'days',  label: msg`Số ngày (theo lịch)`, kind: 'number' },
      // Chỉ có nghĩa khi trường bật «kèm giờ» — không bật thì kernel trả undefined.
      { suffix: 'hours', label: msg`Số giờ`,              kind: 'number', requiresTime: true },
    ],
  },

  // Lựa chọn — lấy đúng options của chính field đó
  select: { kind: 'options' },
  radio: { kind: 'options' },
  multi_select: { kind: 'options', multiValue: true },

  checkbox: { kind: 'bool' },

  user_select: { kind: 'user' },
  department_select: { kind: 'team' },

  // CRM / Tiện ích — picker theo endpoint, lưu id
  account_select: REL('/crm/accounts'),
  customer_select: REL('/crm/accounts'),
  company_select: REL('/crm/companies'),
  contact_select: REL('/crm/contacts'),
  deal_select: REL('/crm/deals'),
  project_select: REL('/tasks/projects'),
  task_select: REL('/tasks', 'title'),
  invoice_select: REL('/crm/invoices', 'invoice_number'),
  payment_select: REL('/crm/payments', 'payment_number'),
  supplier_select: REL('/suppliers'),

  // Không so sánh được (mảng file / object / card read-only)
  image: { kind: null, reason: 'files' },
  file: { kind: null, reason: 'files' },
  out_evidence: { kind: null, reason: 'files' },
  table: { kind: null, reason: 'table' },
  location: { kind: null, reason: 'object' },
  attendance_lookup: { kind: null, reason: 'object' },
  leave_balance: { kind: null, reason: 'object' },
  // reference lưu id đơn được tham chiếu — danh sách chọn phụ thuộc `ref_template_id`
  // của chính field nên không có endpoint tĩnh; để so chuỗi id.
  reference: { kind: 'text' },
};

/**
 * Cách nhập giá trị cho 1 field trong rule điều kiện.
 * Trả `null` khi field không dùng được (bố cục, system field, hoặc kind: null).
 */
export function getConditionValueInput(field) {
  if (!field || !field.type) return null;
  if (field.is_system) return null;
  const cfg = FIELD_TYPE_BY_VALUE[field.type];
  if (cfg?.isLayout) return null;
  const spec = CONDITION_VALUE_INPUT[field.type];
  // Loại field lạ (dữ liệu cũ / addon) → cho so chuỗi, đừng ẩn mất rule đã lưu.
  if (!spec) return { kind: 'text' };
  return spec.kind ? spec : null;
}

/** Field có dùng được trong node Điều kiện không? */
export function isConditionableField(field) {
  return !!getConditionValueInput(field);
}

/**
 * Tách `rule.field_id` dạng đường dẫn `<id trường>.<thuộc tính>`.
 * ⚠️ Phải MIRROR cách tách của kernel (`resolveFieldValue`): cắt ở dấu chấm CUỐI CÙNG, và
 * `i <= 0` thì coi là không có đường dẫn (id mở đầu bằng dấu chấm là id thật, không phải path).
 */
export function splitConditionFieldId(fieldId) {
  const id = String(fieldId == null ? '' : fieldId);
  const i = id.lastIndexOf('.');
  if (i <= 0) return { baseId: id, prop: null };
  return { baseId: id.slice(0, i), prop: id.slice(i + 1) };
}

const findFieldById = (formFields, id) => (formFields || []).find(f => f && f.id === id) || null;

/** Thuộc tính bung ra của 1 field (đã lọc theo cờ «kèm giờ»), [] nếu field không bung. */
function conditionPartsOf(field) {
  const parts = CONDITION_VALUE_INPUT[field?.type]?.parts;
  if (!parts) return [];
  return parts.filter(p => !p.requiresTime || !!field.with_time);
}

/**
 * Cách nhập giá trị cho `rule.field_id` — hiểu cả đường dẫn `<id>.<thuộc tính>`.
 * Khoá NGUYÊN VẸN được ưu tiên, giống kernel: mẫu nhập từ nơi khác có thể có id chứa dấu chấm.
 */
export function getConditionValueInputForField(fieldId, formFields) {
  const direct = findFieldById(formFields, fieldId);
  if (direct) return getConditionValueInput(direct);
  const { baseId, prop } = splitConditionFieldId(fieldId);
  if (!prop) return null;
  const base = findFieldById(formFields, baseId);
  if (!base) return null;
  const part = conditionPartsOf(base).find(p => p.suffix === prop);
  return part ? { kind: part.kind } : null;
}

/** Nhãn hiển thị cho `rule.field_id`, kể cả đường dẫn → "Khoảng TG nghỉ · Số ngày". */
export function conditionFieldLabel(fieldId, formFields) {
  const direct = findFieldById(formFields, fieldId);
  if (direct) return direct.label || fieldId;
  const { baseId, prop } = splitConditionFieldId(fieldId);
  const base = prop ? findFieldById(formFields, baseId) : null;
  if (!base) return fieldId;
  const part = conditionPartsOf(base).find(p => p.suffix === prop);
  return part ? `${base.label} · ${tLabel(part.label)}` : fieldId;
}

/**
 * Danh sách trường chọn được trong 1 rule điều kiện — đã BUNG date_range thành các thuộc
 * tính, và đã loại các loại không so sánh được (ảnh/tệp/bảng/GPS/card…).
 */
export function buildConditionFieldOptions(formFields) {
  const out = [];
  for (const f of formFields || []) {
    if (!f || f.is_system) continue;
    if (FIELD_TYPE_BY_VALUE[f.type]?.isLayout) continue;
    const parts = conditionPartsOf(f);
    if (parts.length) {
      for (const p of parts) out.push({ value: `${f.id}.${p.suffix}`, label: `${f.label} · ${tLabel(p.label)}` });
      continue;
    }
    if (getConditionValueInput(f)) out.push({ value: f.id, label: f.label });
  }
  return out;
}

/**
 * Thay MỌI MessageDescriptor lồng bên trong bằng chuỗi đã dịch (đi sâu qua object/mảng).
 * `tLabel` trả về CHÍNH giá trị cũ khi không phải descriptor → so sánh tham chiếu là đủ
 * để phân biệt, không cần tự cài lại isMessageDescriptor.
 *
 * EXPORT vì có BA cửa đưa khai báo trường vào form state, không phải một: trường mới kéo từ
 * bảng thành phần (`buildNewField`), trường KHOÁ theo loại đơn (`applyKindLockedFields` /
 * `ensureKindLockedFields` ở integrationKinds.js) và trường của MẪU SẴN (`applyPreset` ở
 * TemplateBuilderPage). Hai cửa sau trước đây bê thẳng khai báo ⇒ chọn 1 trong 12 mẫu sẵn rồi
 * mở tab Form là TRẮNG TRANG (React #31: `{id:"AgLgSy"}` lọt vào JSX), và ô «Mô tả» hiện
 * `[object Object]` (phát hiện 15.09.2026 khi bấm tay trên hub).
 */
export function resolveDescriptors(v) {
  if (Array.isArray(v)) return v.map(resolveDescriptors);
  if (v && typeof v === 'object') {
    const s = tLabel(v);
    if (s !== v) return s;                    // chính nó là descriptor → chuỗi
    const out = {};
    for (const k of Object.keys(v)) out[k] = resolveDescriptors(v[k]);
    return out;
  }
  return v;
}

/**
 * Dựng 1 field mới cho builder. form_fields được LƯU XUỐNG DB nên mọi nhãn phải là
 * CHUỖI THẬT: `cfg.label` và các nhãn trong `defaults()` (`options[].label`,
 * `columns[].label`, `content`) đều là `msg`…`` — bản production strip `message`, chỉ
 * còn `{id:"…"}`. Ghi thẳng object đó xuống DB thì mọi lần render sau là React error #31.
 */
export function buildNewField(type, id) {
  const cfg = FIELD_TYPE_BY_VALUE[type];
  return resolveDescriptors({
    id, type, label: cfg?.label || type,
    required: false, placeholder: '',
    ...(cfg?.defaults?.() || {}),
  });
}

// Nhóm của panel «Thành phần». `key` khớp `FIELD_TYPES[].group` (khoá máy, không lưu DB, không dịch);
// `label` là descriptor ⇒ đọc bằng i18n._(g.label). Trước 10/10/2026 nhóm là chuỗi Việt trần dùng
// làm cả khoá lẫn nhãn ⇒ màn tiếng Anh in "Nhập liệu / Thời gian…" ngay giữa trình dựng mẫu.
export const FIELD_GROUPS = [
  { key: 'input', label: msg({ message: 'Nhập liệu', context: 'nhóm trường biểu mẫu' }) },
  { key: 'time', label: msg({ message: 'Thời gian', context: 'nhóm trường biểu mẫu' }) },
  { key: 'choice', label: msg({ message: 'Lựa chọn', context: 'nhóm trường biểu mẫu' }) },
  { key: 'people', label: msg`Người & Phòng ban` },
  { key: 'files', label: msg({ message: 'Tệp', context: 'nhóm trường biểu mẫu' }) },
  { key: 'location', label: msg({ message: 'Vị trí', context: 'nhóm trường biểu mẫu' }) },
  { key: 'advanced', label: msg({ message: 'Nâng cao', context: 'nhóm trường biểu mẫu' }) },
  { key: 'layout', label: msg({ message: 'Bố cục', context: 'nhóm trường biểu mẫu' }) },
  { key: 'crm', label: msg`CRM` },
  { key: 'utility', label: msg({ message: 'Tiện ích', context: 'nhóm trường biểu mẫu' }) },
];

/**
 * System fields — mặc định có sẵn ở mọi template, không xóa được.
 * - id bắt đầu bằng "_" để tránh trùng với custom field id
 * - is_system: true · always_on: true thì không tắt được
 * - Lưu giá trị vào form_data._<id> như field thường
 */
export const SYSTEM_FIELDS = [
  { id: '_title', label: msg`Tên đề xuất`, type: '_title', icon: Heading2, always_on: true, required: true },
  { id: '_assignee', label: msg`Người thực hiện`, type: '_assignee', icon: UserIcon, always_on: true, required: false, auto_assign_requester: true, assignee_self_only: false },
  { id: '_followers', label: msg`Theo dõi (CC)`, type: '_followers', icon: Eye, always_on: false, required: false },
  { id: '_priority', label: msg`Mức độ ưu tiên`, type: '_priority', icon: Flag, always_on: false, required: false },
  { id: '_description', label: msg`Mô tả`, type: '_description', icon: AlignLeft, always_on: false, required: false },
  { id: '_attachment', label: msg`Tệp đính kèm`, type: '_attachment', icon: Paperclip, always_on: false, required: false },
];

export const SYSTEM_FIELD_BY_ID = Object.fromEntries(SYSTEM_FIELDS.map(f => [f.id, f]));

export const PRIORITY_OPTIONS = [
  { value: 'low', label: msg`Thấp`, color: 'bg-gray-100 text-gray-600 border-gray-200' },
  { value: 'normal', label: msg`Bình thường`, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'high', label: msg`Cao`, color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: 'urgent', label: msg`Khẩn cấp`, color: 'bg-red-50 text-red-700 border-red-200' },
];

// `symbol`: hậu tố trong ô NHẬP (FieldInput). Dòng hiển thị đi `fmtCurrency` (Intl đặt ký hiệu theo ngôn ngữ).
// VND dùng ký hiệu tiền «₫» như fmtMoney — không phải chữ cái «đ» (trước 12/10/2026).
export const CURRENCIES = [
  { value: 'VND', label: msg`VNĐ`, symbol: KY_HIEU_TIEN, decimals: 0 },
  // CAD: ký hiệu «CA$» (không phải «$») — ô hiện ký hiệu SAU con số, cùng một mẫu có thể có cả ô USD.
  { value: 'CAD', label: 'CAD', symbol: 'CA$', decimals: 2 },
  { value: 'USD', label: 'USD', symbol: '$', decimals: 2 },
  { value: 'EUR', label: 'EUR', symbol: '€', decimals: 2 },
  { value: 'CNY', label: 'CNY', symbol: '¥', decimals: 2 },
  { value: 'JPY', label: 'JPY', symbol: '¥', decimals: 0 },
];

// ── Tiền tệ mặc định của ô «Số tiền» MỚI ──
// = tiền tệ của workspace (`tenant_settings.default_currency`, Site Connect = CAD). Trang dựng mẫu
// nạp một lần rồi gọi setWorkspaceCurrency(). Hệ không seed khoá đó (Work, Core) hoặc mã ngoài
// CURRENCIES ⇒ giữ 'VND' như trước. CHỈ áp cho ô vừa DỰNG (thêm từ panel «Thành phần», mẫu sẵn,
// ô khoá theo loại đơn) — ô đã lưu giữ nguyên tiền tệ của nó (FieldInput/FieldDisplay vẫn coi
// ô thiếu `currency` là VND, đúng với dữ liệu cũ).
export const FALLBACK_CURRENCY = 'VND';
let workspaceCurrency = null;
export function setWorkspaceCurrency(code) {
  const c = typeof code === 'string' ? code.trim().toUpperCase() : '';
  workspaceCurrency = CURRENCIES.some(x => x.value === c) ? c : null;
}
export function defaultCurrency() {
  return workspaceCurrency || FALLBACK_CURRENCY;
}
/** Ô «Số tiền» vừa dựng từ khai báo sẵn (mẫu sẵn / ô khoá — khai cứng 'VND') ⇒ đổi sang tiền tệ workspace. */
export function withDefaultCurrency(field) {
  // Cột «Số tiền» trong ô Bảng (mẫu «Đề xuất mua sắm»: «Đơn giá») — không thì cột hiện « đ» cạnh ô tổng bằng tiền workspace.
  if (workspaceCurrency && field?.type === 'table' && Array.isArray(field.columns)) {
    const columns = field.columns.map(withDefaultCurrency);
    return columns.some((c, i) => c !== field.columns[i]) ? { ...field, columns } : field;
  }
  // Ô «Công thức» định dạng số tiền cũng mang mã tiền (formatFormulaValue) — không thì luôn hiện « đ».
  if (!workspaceCurrency || !field || (field.type !== 'amount' && field.type !== 'formula')) return field;
  if (field.currency && field.currency !== FALLBACK_CURRENCY) return field;
  return { ...field, currency: workspaceCurrency };
}

/** Default form_fields cho template mới — system fields enabled phù hợp */
export function buildDefaultSystemFormFields() {
  return SYSTEM_FIELDS.map(sf => ({
    id: sf.id,
    type: sf.type,
    // form_fields được LƯU XUỐNG DB → phải là chuỗi thật. `sf.label` là MessageDescriptor,
    // bản production chỉ còn `{id:"…"}` ⇒ ghi thẳng vào DB rồi render là React error #31.
    label: tLabel(sf.label),
    is_system: true,
    enabled: true,
    required: !!sf.required,
    ...(sf.auto_assign_requester !== undefined ? { auto_assign_requester: sf.auto_assign_requester } : {}),
    ...(sf.assignee_self_only !== undefined ? { assignee_self_only: sf.assignee_self_only } : {}),
  }));
}

/**
 * LOẠI ĐƠN mà người thực hiện BẮT BUỘC là người gửi — chuẩn của loại, admin không tắt
 * được. Đơn chỉ nói về bản thân người gửi thì không có lý do chọn người khác.
 *
 * ⚠️ MIRROR của `@cnv/server-shared/src/constants/approvalFields.js`
 * (`ASSIGNEE_SELF_ONLY_KIND_LABELS`) — cnv-ui không import được package BE.
 * Thêm/bớt kind thì phải sửa CẢ HAI. BE mới là chốt chặn thật, FE chỉ khoá giao diện.
 */
// Giá trị là descriptor (đọc bằng i18n._) — chỉ dùng để chèn vào câu giải thích ở FormTab. Tập KHOÁ mới
// là thứ phải khớp BE; nhãn đổi/dịch thoải mái.
export const ASSIGNEE_SELF_ONLY_KIND_LABELS = {
  leave: msg`nghỉ phép`,
  late_arrival: msg`xin đi làm trễ`,
  early_leave: msg`xin về sớm`,
  attendance_fix: msg`bổ sung chấm công`,
  wfh: msg`làm việc tại nhà`,
};

export function isAssigneeSelfOnlyKind(kind) {
  return !!kind && Object.prototype.hasOwnProperty.call(ASSIGNEE_SELF_ONLY_KIND_LABELS, kind);
}

/**
 * Mẫu có KHOÁ "Người thực hiện" về đúng người gửi đơn không?
 *
 * 2 tầng: kind thuộc nhóm đơn cá nhân → LUÔN khoá; kind khác → theo công tắc admin bật
 * trên chính system field `_assignee` (`assignee_self_only: true`, mặc định TẮT).
 */
export function isAssigneeSelfOnly(form_fields = [], kind) {
  if (isAssigneeSelfOnlyKind(kind)) return true;
  const f = findAssigneeField(form_fields);
  if (!f || f.enabled === false) return false;
  return f.assignee_self_only === true;
}

/**
 * Gắn cờ khoá theo kind vào form_fields trước khi render (form gửi đơn + xem trước
 * builder). Cần vì mẫu cũ chưa lưu lại thì trong DB chưa có cờ, nhưng BE vẫn chặn theo
 * kind ⇒ không gắn thì ô vẫn cho chọn rồi gửi mới báo lỗi.
 */
export function withKindAssigneeLock(form_fields = [], kind) {
  if (!isAssigneeSelfOnlyKind(kind)) return form_fields;
  const f = findAssigneeField(form_fields);
  if (!f) return form_fields;
  return form_fields.map(x => (x === f
    ? { ...x, assignee_self_only: true, auto_assign_requester: true, enabled: true }
    : x));
}

/** Tìm system field "Người thực hiện" (ưu tiên type, fallback id). */
export function findAssigneeField(form_fields = []) {
  if (!Array.isArray(form_fields)) return null;
  return form_fields.find(f => f && f.type === '_assignee')
    || form_fields.find(f => f && f.id === '_assignee')
    || null;
}

/**
 * Gộp field trùng `id` — giữ MỘT bản, ưu tiên bản gắn `is_kind_locked` (bản canonical
 * theo loại đơn, cũng là bản admin sửa được trong builder).
 *
 * Vì sao phải có: `id` của field là `key` của React khi render danh sách. Hai field
 * cùng id ⇒ hai key trùng ⇒ React không giữ được danh tính con qua mỗi lần cập nhật,
 * nên ô nhập KHÔNG vẽ lại giá trị vừa chọn — dữ liệu vào `formData` bình thường (bộ
 * đếm "còn N trường bắt buộc" vẫn tụt) mà mắt người dùng thấy ô ngày trống trơn:
 * "chọn ngày mà không nhảy ngày". Đã đo trên prod 28/08/2026 (2 mẫu wfh +
 * attendance_fix của 1 tenant, mỗi field kind bị nhân đôi).
 *
 * Gốc đã vá ở kernel `@cnv/server-shared/business-logic/approvalKindLockedFields`
 * (ensureKindLockedFields) + kho mẫu; đây là lưới an toàn cho dữ liệu CŨ còn trong DB.
 */
export function dedupeFieldsById(form_fields = []) {
  if (!Array.isArray(form_fields)) return form_fields;
  const byId = new Map();
  for (const f of form_fields) {
    if (!f || !f.id) continue;
    const prev = byId.get(f.id);
    if (!prev) { byId.set(f.id, f); continue; }
    if (!prev.is_kind_locked && f.is_kind_locked) byId.set(f.id, f);
  }
  if (byId.size === form_fields.length) return form_fields;
  // eslint-disable-next-line no-console
  console.warn('[approvals] mẫu có field trùng id — đã gộp để form không kẹt:',
    form_fields.filter(f => f && f.id).map(f => f.id).filter((id, i, a) => a.indexOf(id) !== i));
  return [...byId.values()];
}

/** Khi load template cũ chưa có system fields, merge vào đầu form_fields */
export function ensureSystemFields(form_fields = []) {
  const fields = dedupeFieldsById(form_fields);
  const ids = new Set(fields.map(f => f.id));
  const missing = SYSTEM_FIELDS.filter(sf => !ids.has(sf.id)).map(sf => ({
    id: sf.id, type: sf.type, label: tLabel(sf.label),
    is_system: true, enabled: sf.always_on, required: !!sf.required,
  }));
  return [...missing, ...fields];
}

/** Tách form_fields thành { systemFields, customFields } theo thứ tự ban đầu */
export function splitSystemFields(form_fields = []) {
  const systemFields = [];
  const customFields = [];
  form_fields.forEach(f => (f.is_system ? systemFields : customFields).push(f));
  return { systemFields, customFields };
}
